import {formatMoney} from '@shopify/theme-currency';
import serialize from 'form-serialize';

interface ProductData {
  options: string[];
  variants: ProductVariant[];
}

interface OptionValuePair {
  name: string;
  value: string;
}

interface ProductVariant {
  available: boolean;
  id: number;
  price: number;
  options: string[];
  option1: string;
  option2: string;
  option3: string;
}

export interface ProductFormOptions {
  optionSelector: string;
  priceSelector: string;
  selectSelector: string;
  submitSelector: string;
  submitTextSelector: string;
  wishlistSelector: string;
  backInStockSelector: string;
  checkStockSelector: string;
  productData: ProductData;
}

type Options = Partial<ProductFormOptions>;

export class ProductForm {
  static defaults = {
    backInStockSelector: '.swym-isa-inline-form',
    checkStockSelector: '.check-stock',
    labelAddToCart: 'Add To Cart',
    labelSoldOut: 'Sold Out',
    optionSelector: '[data-option]',
    priceSelector: '[data-product-price]',
    productData: {variants: [], options: []},
    selectSelector: '[data-option-select]',
    submitSelector: '[data-ajaxadd-button]',
    submitTextSelector: '[data-ajaxadd-button-text]',
    wishlistSelector: '.product__addToWishlist',
  };
  container: HTMLFormElement;
  select: HTMLSelectElement;
  optionElements: HTMLInputElement[];
  options: ProductFormOptions;
  optionNames: string[];
  priceElement: HTMLElement;
  submitElement: HTMLFormElement;
  submitTextElement: HTMLElement;
  wishlistElement: HTMLElement;
  backInStockElement: HTMLElement;
  checkStockElement: HTMLElement;

  constructor(container: HTMLFormElement, options: Options) {
    if (!container) {
      return;
    }

    this.container = container;
    this.options = {
      ...ProductForm.defaults,
      ...this.container.dataset,
      ...options,
    };

    const priceElement = container.querySelector(
      this.options.priceSelector,
    ) as HTMLElement;

    if (priceElement) {
      this.priceElement = priceElement;
    }

    const selector = container.querySelector(
      this.options.selectSelector,
    ) as HTMLSelectElement;

    const submitElement = container.querySelector(
      this.options.submitSelector,
    ) as HTMLFormElement;

    if (submitElement) {
      this.submitElement = submitElement;
    }

    const submitTextElement = container.querySelector(
      this.options.submitTextSelector,
    ) as HTMLElement;

    if (submitTextElement) {
      this.submitTextElement = submitTextElement;
    }

    const wishlistElement = container.querySelector(
      this.options.wishlistSelector,
    ) as HTMLElement;

    if (wishlistElement) {
      this.wishlistElement = wishlistElement;
    }

    const backInStockElement = container.querySelector(
      this.options.backInStockSelector,
    ) as HTMLElement;

    if (backInStockElement) {
      this.backInStockElement = backInStockElement;
    }

    const checkStockElement = container.querySelector(
      this.options.checkStockSelector,
    ) as HTMLElement;

    if (checkStockElement) {
      this.checkStockElement = checkStockElement;
    }

    if (!selector || !this.options.productData) {
      return;
    }

    this.select = selector;
    this.optionNames = this.options.productData.options;
    this.optionElements = Array.from(
      this.container.querySelectorAll(this.options.optionSelector),
    );

    this.bind();
    this.disableUnavailable();
  }

  bind() {
    this.container.addEventListener('change', this.handleChange.bind(this));
  }

  handleChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const matches = target.closest(this.options.optionSelector);

    if (!matches) {
      return;
    }

    const variant = this.getVariant();
    this.disableUnavailable(target.value, variant);
  }

  getCurrentOptions() {
    const formData = serialize(this.container, {
      hash: true,
    });

    return Object.values(formData.options || []);
  }

  getVariant(): ProductVariant | undefined {
    const values = this.getCurrentOptions();
    return getVariantByOptions(values, this.options.productData);
  }

  setVariant(variant: ProductVariant) {
    const {id = ''} = variant;

    this.select.value = id.toString();

    this.setPrice(variant.price);
    this.setVariantUrl(variant);
    this.setButton(variant);
    this.enableWishlist();
    this.unblockBackInStock();
    this.unblockCheckStock();

    const event = new CustomEvent('optionsChange', {
      bubbles: true,
      detail: {
        variant,
      },
    });

    this.container.dispatchEvent(event);
  }

  unblockBackInStock() {
    if (
      this.backInStockElement &&
      this.backInStockElement.classList.contains(
        'swym-isa-inline-form--initial',
      )
    ) {
      this.backInStockElement.classList.remove('swym-isa-inline-form--initial');
    }
  }

  unblockCheckStock() {
    if (
      this.backInStockElement &&
      this.backInStockElement.classList.contains('check-stock-hidden')
    ) {
      this.backInStockElement.classList.remove('check-stock-hidden');
    }
  }

  enableWishlist() {
    if (
      this.wishlistElement &&
      this.wishlistElement.classList.contains(
        'product__addToWishlist--disabled',
      )
    ) {
      this.wishlistElement.classList.remove('product__addToWishlist--disabled');
    }
  }

  setPrice(price: number) {
    const target = this.priceElement;

    if (!target) {
      return;
    }

    this.priceElement.innerHTML = formatMoney(
      price,
      target.dataset.moneyFormat,
    );
  }

  setButton(variant: ProductVariant) {
    const {available = ''} = variant;
    const labelAddToCart =
      this.submitElement.dataset.labelAddToCart ||
      ProductForm.defaults.labelAddToCart;
    const labelSoldOut =
      this.submitElement.dataset.labelSoldOut ||
      ProductForm.defaults.labelSoldOut;

    if (!available || !this.submitElement || !this.submitTextElement) {
      this.submitElement.setAttribute('disabled', 'true');
      this.submitTextElement.innerHTML = labelSoldOut;
    } else {
      this.submitElement.removeAttribute('disabled');
      this.submitTextElement.innerHTML = labelAddToCart;
    }

    this.submitElement.classList.remove('hidden');
  }

  setVariantUrl(variant: ProductVariant) {
    const {id = ''} = variant;

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('variant', id.toString());
    window.history.replaceState({}, '', `${location.pathname}?${searchParams}`);

    const hiddenSwymBackInStockElement = document.querySelector(
      '.swym-isa-inline-form.current-variant-unavailable',
    );
    if (hiddenSwymBackInStockElement) {
      hiddenSwymBackInStockElement.classList.remove(
        'current-variant-unavailable',
      );
    }

    const hiddenCheckStockElement = document.querySelector('.check-stock');
    if (hiddenCheckStockElement) {
      hiddenCheckStockElement.classList.remove('check-stock-hidden');
    }
  }

  getVariantMap(): OptionValuePair[][] {
    return this.options.productData.variants.map(product =>
      this.optionNames.map((name, index) => {
        return {
          name: `options[${name}]`,
          value: product.options[index],
        };
      }),
    );
  }

  getClosestAvailableVariant(
    option: string,
    currentVariant?: ProductVariant,
  ): ProductVariant | undefined {
    return this.options.productData.variants.find(product => {
      const options = product.options;
      const isSameVariant = options.every(val => {
        if (!currentVariant) {
          return false;
        }

        return currentVariant.options.includes(val);
      });

      if (!options.includes(option) || isSameVariant || !product.available) {
        return false;
      }

      return true;
    });
  }

  setProductOptions(options: string[]) {
    const targets = this.optionElements.filter(opt =>
      options.includes(opt.value),
    );

    // Handle input type
    targets.forEach(element => (element.checked = true));
  }

  /**
   * Returns an array of option names
   */
  getOptionsToDisable(option: string) {
    // Find every combination with the given value
    return this.options.productData.variants.map(product => {
      const options = product.options;

      if (!options.includes(option)) {
        return;
      }

      return product;
    });
  }

  // pruneOptions(options) {

  // }

  disableUnavailable(
    value?: string,
    variant: ProductVariant | undefined = this.getVariant(),
  ) {
    const selectedOption = value || (variant && variant.option1);

    if (!selectedOption) {
      return;
    }

    // -> Get all option data
    // -> on change: .some(variants, selected option + each combination of options)
    // disable matches, enable unmatches, if disabled match is selected -> select next value

    if (!variant) {
      const closest = this.getClosestAvailableVariant(selectedOption, variant);

      // No closest variant available
      if (!closest) {
        return;
      }

      this.setVariant(closest);
      this.setProductOptions(closest.options);
      return;
    }

    this.setVariant(variant);

    // const disabledOptions = this.getOptionsToDisable(selectedOption);
    // this.pruneOptions(disabledOptions);

    // const matchData =

    // // const variants = this.options.productData.variants;

    // if (!current || current.available) {
    //   return;
    // }

    // const mappedOptions = this.optionNames.map((name, index) => {
    //   return {
    //     name: `options[${name}]`,
    //     value: current.options[index],
    //   };
    // });

    // this.optionElements.forEach(node => {
    //   mappedOptions.forEach(item => {
    //     if (node.name === item.name && node.value === item.value) {
    //       node.disabled = true;
    //     } else {
    //       node.disabled = false;
    //     }
    //   });
    // });
  }
}

const getVariantByOptions = (
  values: any,
  data: ProductData,
): ProductVariant | undefined => {
  const {variants = []} = data;

  return variants.find((variant: ProductVariant) =>
    variant.options.every(opt => values.includes(opt)),
  );
};
